const {createApp, defineComponent} = Vue

const StarIcon = defineComponent({
  name: 'StarIcon',
  props: {
    color: {
      type: String,
      default: '#000000'
    },
    width: {
      type: Number,
      default: 18
    },
    height: {
      type: Number,
      default: 16
    },
    type: {
      type: String,
      default: 'full'
    }
  },
  template: `
    <!--!Font Awesome Free 6.6.0 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2024 Fonticons, Inc.-->
    <svg
      v-if="type === 'half'"
      xmlns="http://www.w3.org/2000/svg"
      :fill="color"
      :height="height"
      :width="width"
      viewBox="0 0 576 512">
      <path
        d="M309.5 13.5C305.5 5.2 297.1 0 287.9 0s-17.6 5.2-21.6 13.5L197.7 154.8 44.5 177.5c-9 1.3-16.5 7.6-19.3 16.3s-.5 18.1 5.9 24.5L142.2 328.4 116 483.9c-1.5 9 2.2 18.1 9.7 23.5s17.3 6 25.3 1.7l137-73.2 137 73.2c8.1 4.3 17.9 3.7 25.3-1.7s11.2-14.5 9.7-23.5L433.6 328.4 544.8 218.2c6.5-6.4 8.7-15.9 5.9-24.5s-10.3-14.9-19.3-16.3L378.1 154.8 309.5 13.5zM288 384.7l0-305.6 52.5 108.1c3.5 7.1 10.2 12.1 18.1 13.3l118.3 17.5L391 303c-5.5 5.5-8.1 13.3-6.8 21l20.2 119.6L299.2 387.5c-3.5-1.9-7.4-2.8-11.2-2.8z"/>
    </svg>
    <svg
      v-else-if="type === 'empty'"
      xmlns="http://www.w3.org/2000/svg"
      :fill="color"
      :height="height"
      :width="width"
      viewBox="0 0 576 512">
      <path
        d="M287.9 0c9.2 0 17.6 5.2 21.6 13.5l68.6 141.3 153.2 22.6c9 1.3 16.5 7.6 19.3 16.3s.5 18.1-5.9 24.5L433.6 328.4l26.2 155.6c1.5 9-2.2 18.1-9.7 23.5s-17.3 6-25.3 1.7l-137-73.2L151 509.1c-8.1 4.3-17.9 3.7-25.3-1.7s-11.2-14.5-9.7-23.5l26.2-155.6L31.1 218.2c-6.5-6.4-8.7-15.9-5.9-24.5s10.3-14.9 19.3-16.3l153.2-22.6L266.3 13.5C270.4 5.2 278.7 0 287.9 0zm0 79L235.4 187.2c-3.5 7.1-10.2 12.1-18.1 13.3L99 217.9 184.9 303c5.5 5.5 8.1 13.3 6.8 21L171.4 443.7l105.2-56.2c7.1-3.8 15.6-3.8 22.6 0l105.2 56.2L384.2 324.1c-1.3-7.7 1.2-15.5 6.8-21l85.9-85.1L358.6 200.5c-7.8-1.2-14.6-6.1-18.1-13.3L287.9 79z"/>
    </svg>
    <svg
      v-else
      xmlns="http://www.w3.org/2000/svg"
      :fill="color"
      :height="height"
      :width="width"
      viewBox="0 0 576 512"
    >
      <path
        d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"/>
    </svg>
  `
})

const Pagination = defineComponent({
  name: 'Pagination',
  emits: ['page-changed'],
  props: {
    totalPages: {
      type: Number,
      required: true
    },
    currentPage: {
      type: Number,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  methods: {
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.$emit('page-changed', page);
      }
    }
  },
  template: `
    <div>
      <button
        :disabled="currentPage === 1 || loading"
        @click="changePage(1)">
        «
      </button>
      <button
        :disabled="currentPage === 1 || loading"
        @click="changePage(currentPage - 1)">
        ‹
      </button>

      <template v-if="totalPages <= 5">
        <button
          v-for="page in totalPages"
          :key="page"
          :disabled="loading"
          :class="{ active: page === currentPage }"
          @click="changePage(page)">
          {{ page }}
        </button>
      </template>
      <template v-else-if="currentPage <= 3">
        <button
          v-for="page in 3"
          :key="page"
          :disabled="loading"
          :class="{ active: page === currentPage }"
          @click="changePage(page)">
          {{ page }}
        </button>
        <button
          :disabled="loading"
          :class="{ active: 4 === currentPage }"
          @click="changePage(4)">
          {{ 4 }}
        </button>
        <button
          disabled>
          …
        </button>
      </template>
      <template v-else-if="(totalPages - currentPage) <= 3">
        <button
          disabled>
          …
        </button>
        <button
          :class="{ active: totalPages - 3 === currentPage }"
          :disabled="loading"
          @click="changePage(totalPages - 3)">
          {{ totalPages - 3 }}
        </button>
        <button
          v-for="page in [totalPages - 2, totalPages - 1, totalPages]"
          :key="page"
          :disabled="loading"
          :class="{ active: page === currentPage }"
          @click="changePage(page)">
          {{ page }}
        </button>
      </template>
      <template v-else>
        <button disabled>
          …
        </button>
        <button
          :disabled="loading"
          @click="changePage(currentPage - 1)">
          {{ currentPage - 1 }}
        </button>
        <button
          :disabled="loading"
          :class="{ active: true }"
          @click="changePage(currentPage)">
          {{ currentPage }}
        </button>
        <button
          :disabled="loading"
          @click="changePage(currentPage + 1)">
          {{ currentPage + 1 }}
        </button>
        <button disabled>
          …
        </button>
      </template>

      <button
        :disabled="currentPage === totalPages || loading"
        @click="changePage(currentPage + 1)">
        ›
      </button>
      <button
        :disabled="currentPage === totalPages || loading"
        @click="changePage(totalPages)">
        »
      </button>
    </div>
  `
})

const ProductReviewStars = defineComponent({
  name: 'ProductReviewStars',
  components: {
    StarIcon,
  },
  props: {
    rating: {
      type: Number,
      required: true,
    },
  },
  computed: {
    stars() {
      const full = Math.floor(this.rating)
      const half = Math.ceil(this.rating) - full
      return {
        full,
        half,
        empty: 5 - full - half,
      }
    }
  },
  template: `
    <div class="product-reviews__stars">
      <i v-for="i in stars.full" :key="i">
        <star-icon color="var(--highlight-color)" height="16" width="18"/>
      </i>
      <i v-for="i in stars.half" :key="i">
        <star-icon color="var(--highlight-color)" height="16" width="18" type="half"/>
      </i>
      <i v-for="i in stars.empty" :key="i">
        <star-icon color="var(--highlight-color)" height="16" width="18" type="empty"/>
      </i>
    </div>`
})

const ProductReview = defineComponent({
  name: "ProductReview",
  components: {
    ProductReviewStars,
  },
  props: {
    review: {
      type: Object,
      required: true,
    },
  },
  computed: {
    formattedDate() {
      const locale = navigator.language
      return new Date(this.review.attributes.created_at.date).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
  },
  template: `
    <article class="product-reviews__review">
      <div class="product-reviews__review__header-container">
        <div class="product-reviews__review__header">
          <product-review-stars :rating="review.attributes.rating"/>
          <div class="product-reviews__review__author-name">{{ review.attributes.reviewer }}</div>
        </div>
        <div class="product-reviews__review__date">{{ formattedDate }}</div>
      </div>
      <p class="product-reviews__review__text">{{ review.attributes.review }}</p>
    </article>`
})

const RatingSummary = defineComponent({
  name: "RatingSummary",
  components: {
    ProductReviewStars,
    StarIcon,
  },
  props: {
    avgRating: {
      type: Number,
      required: true,
    },
    totalReviews: {
      type: Number,
      required: true,
    },
    ratingCounts: {
      type: Object,
      required: true,
    },
  },
  methods: {
    starsPercentage(star) {
      return this.ratingCounts[star] / this.totalReviews * 100;
    }
  },
  template: `
    <div class="product-reviews__summary">
      <div class="product-reviews__summary__header">
        <div class="product-reviews__summary__avg">
          {{ avgRating }}
        </div>
        <div>
          <product-review-stars :rating="avgRating"/>
          <span class="product-reviews__summary__total">{{ totalReviews }} total ratings</span>
        </div>
      </div>
      <table class="product-reviews__summary__bars">
        <tr v-for="i in [5, 4, 3, 2, 1]" :key="i">
          <td>
            <div>
              <div class="product-reviews__summary__bar" :style="{ width: starsPercentage(i) + '%' }">&nbsp;</div>
            </div>
          </td>
          <td>
            {{ i }}
            <star-icon color="#000000" height="10" width="10"/>
          </td>
        </tr>
      </table>
    </div>`
})

const RatingFilter = defineComponent({
  name: "RatingFilter",
  components: {
    StarIcon,
  },
  emits: ['select'],
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      show: false,
      selected: null
    };
  },
  methods: {
    toggleDropdown() {
      this.show = !this.show;
      if (this.show) {
        document.addEventListener('click', this.closeDropdown);
      }
    },
    closeDropdown(event) {
      if (!this.$el.contains(event.target)) {
        this.show = false;
        document.removeEventListener('click', this.closeDropdown);
      }
    },
    selectOption(option) {
      this.selected = option;
      this.$emit('select', option);
      this.show = false;
      document.removeEventListener('click', this.closeDropdown);
    }
  },
  template: `
    <div class="product-reviews__rating-filter" @click="toggleDropdown">
      <button class="button button--secondary" :disabled="disabled">
        {{ selected >= 1 && selected <= 5 ? 'Rating (' + selected + ')' : 'Rating' }}
      </button>
      <div class="product-reviews__rating-filter__content" :class="{ show: show }">
        <a v-for="option in ['All', 5, 4, 3, 2, 1]" :key="option" @click.stop="selectOption(option)">
          {{ option }}
          <star-icon v-if="option >= 1" color="#000000" height="10" width="10"/>
        </a>
      </div>
    </div>
  `,
})

const ProductReviews = defineComponent({
  name: "ProductReviews",
  components: {
    ProductReview,
    ProductReviewStars,
    StarIcon,
    RatingSummary,
    Pagination,
    RatingFilter,
  },
  data() {
    return {
      endpoint: 'https://vcloud.velovita.com/v1/shopify_reviews',
      reviews: undefined,
      currentPage: 1,
      loading: false,
      currentRating: null,
    }
  },
  mounted() {
    this.getProductReviews().then(() => {});
  },
  methods: {
    productHandle() {
      const url = window.location.href;

      const parsedUrl = new URL(url);

      const segments = parsedUrl.pathname.split('/');
      return segments.pop() || segments.pop();
    },
    getProductReviews(page = 1, rating = null, scroll = false) {
      this.loading = true;
      const previousPage = this.currentPage;
      const previousRating = this.currentRating;
      this.currentPage = page;
      this.currentRating = rating;

      const url = new URL(this.endpoint + '/' + this.productHandle());
      url.searchParams.append('page', page.toString());
      if (rating && rating !== 'All') url.searchParams.append('rating', rating.toString());

      return fetch(url)
        .then(response => response.json())
        .then(data => {
          this.currentPage = data.meta.pagination.current_page;
          this.reviews = data;

          if (scroll) {
            // Scroll to the #product-reviews element
            const position = document.getElementById("product-reviews").getBoundingClientRect().top + window.scrollY;
            const offset = -118;

            window.scrollTo({
              top: position + offset,
              behavior: "smooth"
            });
          }
          return data;
        })
        .catch(error => {
          this.currentPage = previousPage;
          this.currentRating = previousRating;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  template: `
    <div v-if="reviews && reviews.meta">
      <div class="product-reviews__title h2">
        Featured Reviews
      </div>
      <div class="product-reviews__container">
        <rating-summary
          :avg-rating="reviews.meta.average_rating"
          :rating-counts="reviews.meta.rating_counts"
          :total-reviews="reviews.meta.total_reviews"/>
        <section style="width: 100%">
          <rating-filter
            :disabled="loading"
            @select="getProductReviews(1, $event, true)"/>
          <product-review
            v-for="review in reviews.data"
            :key="review.id"
            :review="review"/>
          <div v-if="!loading && reviews.data.length === 0">
            No ratings with reviews found.
          </div>
        </section>
      </div>
      <div class="product-reviews__pagination">
        <pagination
          :loading="loading"
          :current-page="currentPage"
          :total-pages="reviews.meta.pagination.total_pages"
          @pageChanged="getProductReviews($event, currentRating, true)"/>
      </div>
    </div>
  `
})

createApp(ProductReviews).mount('#product-reviews')
